import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { LoaderFullPage } from '@alejandro3c/icy-react-ui';
import Login from '@/features/auth/pages/Login';
import Unauthorized from '@/features/auth/pages/Unauthorized';
import PrivateRoute from '@/shared/components/PrivateRoute';
import RoleBasedRedirect from '@/shared/components/RoleBasedRedirect';
import Layout from '@/shared/components/Layout';
import ErrorBoundary from '@/shared/components/ErrorBoundary';

// Admin pages
const Users = lazy(() => import('@/features/users/pages/Users'));
const EditUserDaysOff = lazy(() => import('@/features/users/pages/EditUserDaysOff'));
const Groups = lazy(() => import('@/features/groups/pages/Groups'));
const AddGroup = lazy(() => import('@/features/groups/pages/AddGroup'));
const EditGroup = lazy(() => import('@/features/groups/pages/EditGroup'));
const Projects = lazy(() => import('@/features/projects/pages/Projects'));
const ArchivedProjects = lazy(() => import('@/features/projects/pages/ArchivedProjects'));
const Opportunities = lazy(() => import('@/features/opportunities/pages/Opportunities'));
const ArchivedOpportunities = lazy(() => import('@/features/opportunities/pages/ArchivedOpportunities'));
const DayOffRequests = lazy(() => import('@/features/day-off/pages/DayOffRequestsPage'));
const DayOffBalance = lazy(() => import('@/features/day-off/pages/DayOffBalancePage'));
const DayOffConfiguration = lazy(() => import('@/features/day-off/pages/DayOffConfigurationPage'));
const WorkActivitieTypes = lazy(() => import('@/features/work-activities/pages/WorkActivityTypes'));
const WorkAnalytics = lazy(() => import('@/features/work-activities/pages/WorkAnalytics'));
const TimeSheets = lazy(() => import('@/features/timesheet/pages/TimeSheets'));
const TimesheetPrintPage = lazy(() => import('@/features/timesheet/pages/TimesheetPrintPage').then((m) => ({ default: m.TimesheetPrintPage })));
const SettingsGooglePage = lazy(() => import('@/features/settings/pages/SettingsGooglePage'));
const SettingsDaysOffPage = lazy(() => import('@/features/settings/pages/SettingsDaysOffPage'));
const Holidays = lazy(() => import('@/features/holidays/pages/Holidays'));

// Web CMS pages (admin + web role)
const WebClients = lazy(() => import('@/features/web/pages/WebClients'));
const AddClient = lazy(() => import('@/features/web/pages/AddClient'));
const EditClient = lazy(() => import('@/features/web/pages/EditClient'));
const Categories = lazy(() => import('@/features/web/pages/Categories'));
const AddCategory = lazy(() => import('@/features/web/pages/AddCategory'));
const EditCategory = lazy(() => import('@/features/web/pages/EditCategory'));
const Credentials = lazy(() => import('@/features/web/pages/Credentials'));
const AddCredential = lazy(() => import('@/features/web/pages/AddCredential'));
const EditCredential = lazy(() => import('@/features/web/pages/EditCredential'));
const PrivateWebs = lazy(() => import('@/features/web/pages/PrivateWebs'));
const AddPrivateWeb = lazy(() => import('@/features/web/pages/AddPrivateWeb'));
const EditPrivateWeb = lazy(() => import('@/features/web/pages/EditPrivateWeb'));

// Worker pages
const UserDayOffRequest = lazy(() => import('@/features/day-off/pages/UserDayOffRequestPage'));
const UserTimesheet = lazy(() => import('@/features/timesheet/pages/UserTimesheet'));
const UserConfig = lazy(() => import('@/features/config/pages/UserConfig'));

// Public pages
const PrivacyPolicy = lazy(() => import('@/features/legal/pages/PrivacyPolicy'));

export default function App() {
	return (
		<ErrorBoundary>
			<Suspense fallback={<LoaderFullPage />}>
				<Routes>
					<Route path="/login" element={<Login />} />
					<Route path="/unauthorized" element={<Unauthorized />} />
					<Route path="/privacy-policy" element={<PrivacyPolicy />} />

					{/* Redirección Dinámica en "/" */}
					<Route path="/" element={<RoleBasedRedirect />} />

					{/* Print page without Layout */}
					<Route element={<PrivateRoute allowedRoles={['admin']} />}>
						<Route path="/timesheet/print/:id" element={<TimesheetPrintPage />} />
					</Route>

					{/* All routes with shared Layout */}
					<Route element={<PrivateRoute allowedRoles={['admin', 'user', 'web']} />}>
						<Route element={<Layout />}>
							{/* Admin only */}
							<Route element={<PrivateRoute allowedRoles={['admin']} />}>
								{/* Acceso */}
								<Route path="/users" element={<Users />} />
								<Route path="/users/:id/daysOff" element={<EditUserDaysOff />} />
								<Route path="/groups" element={<Groups />} />
								<Route path="/groups/new" element={<AddGroup />} />
								<Route path="/groups/edit/:id" element={<EditGroup />} />

								{/* Gestión */}
								<Route path="/projects" element={<Projects />} />
								<Route path="/projects/archived" element={<ArchivedProjects />} />
								<Route path="/opportunities" element={<Opportunities />} />
								<Route path="/opportunities/archived" element={<ArchivedOpportunities />} />

								{/* Vacaciones */}
								<Route path="/dayOffRequests" element={<DayOffRequests />} />
								<Route path="/daysOffBalance" element={<DayOffBalance />} />
								<Route path="/daysOffConfiguration" element={<DayOffConfiguration />} />

								{/* Registro */}
								<Route path="/workActivityTypes" element={<WorkActivitieTypes />} />
								<Route path="/workAnalytics" element={<WorkAnalytics />} />
								<Route path="/timesheet" element={<TimeSheets />} />

								{/* Configuration */}
								<Route path="/settings-google" element={<SettingsGooglePage />} />
								<Route path="/settings-days-off" element={<SettingsDaysOffPage />} />
								<Route path="/settings-holidays" element={<Holidays />} />
							</Route>

							{/* Admin + Web */}
							<Route element={<PrivateRoute allowedRoles={['admin', 'web']} />}>
								<Route path="/webClients" element={<WebClients />} />
								<Route path="/webClients/new" element={<AddClient />} />
								<Route path="/webClients/:id/edit" element={<EditClient />} />
								<Route path="/webCategories" element={<Categories />} />
								<Route path="/webCategories/new" element={<AddCategory />} />
								<Route path="/webCategories/:id/edit" element={<EditCategory />} />
								<Route path="/webCredentials" element={<Credentials />} />
								<Route path="/webCredentials/new" element={<AddCredential />} />
								<Route path="/webCredentials/:id/edit" element={<EditCredential />} />
								<Route path="/webPrivate" element={<PrivateWebs />} />
								<Route path="/webPrivate/new" element={<AddPrivateWeb />} />
								<Route path="/webPrivate/:id/edit" element={<EditPrivateWeb />} />
							</Route>

							{/* User + Web */}
							<Route element={<PrivateRoute allowedRoles={['user', 'web']} />}>
								<Route path="/dayoff" element={<UserDayOffRequest />} />
								<Route path="/user-timesheet" element={<UserTimesheet />} />
								<Route path="/user-config" element={<UserConfig />} />
							</Route>
						</Route>
					</Route>
				</Routes>
			</Suspense>
		</ErrorBoundary>
	);
}
